import React, { useCallback, useMemo, useState } from "react";
import { classNames } from "../../common_util";
import * as Model from "../types/cart";
import scooterAnimatedIconUrl from "images/icons/scooter-animated.svg";
import VisaMarkIconPath from "images/payment-marks/visa.svg"
import MastercardMarkIconPath from "images/payment-marks/mastercard.svg"
import AfterpayMarkIconPath from "images/payment-marks/afterpay.svg"
import GooglePayIconPath from "images/payment-marks/google-pay.svg";
import ApplePayIconPath from "images/payment-marks/apple-pay.svg";
import loadingSpinnerAnimatedIconUrl from "images/icons/loading-spinner-animated.svg";
import { newLookReviewOrderPath } from "../../routes";
import { useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { FulfillmentPopover } from "./fulfillment_popover";
import { Product } from "src/storefront/types/catalog";
import { buildTagElementsForTags } from "src/storefront/components/product_tags";
import { NavigatePage, StorefrontPage, useNavigationState } from "../types/navigation";
import { StreamlineIcon } from "src/components/icons";
import arrowLeftIconUrl from "images/icons/arrow-left.svg";
import bin2IconUrl from "images/icons/bin-2.svg";
import moveExpandVerticalIconUrl from "images/icons/move-expand-vertical.svg";
import pencilIconUrl from "images/icons/pencil.svg";
import shoppingBagSideIconUrl from "images/icons/shopping-bag-side.svg";
import synchronizeArrowsSquareIconUrl from "images/icons/synchronize-arrows-square.svg";
import tagsFavoriteIconUrl from "images/icons/tags-favorite.svg";

const Cart = ({
  cart,
  cartMaxHeightOffset,
  displayingCartAppView,
  storeHidesAgeRestrictedItems,
  editCartItem,
  removeCartItem,
  isLoading,
  addItemToCart
}: {
  cart: Model.Cart | null;
  cartMaxHeightOffset: number;
  isLoading: boolean;
  displayingCartAppView: boolean;
  storeHidesAgeRestrictedItems: boolean;
  editCartItem: (cartItem: Model.CartItem) => void;
  removeCartItem: (cartItem: Model.CartItem) => void;
  addItemToCart: (product: Product) => Promise<void>;
}) : React.JSX.Element => {
  const storefrontConfig = useStorefrontConfig();
  const [submitting, setSubmitting] = useState(false);
  const { catalog } = useStorefrontHelper();
  const [_navigationState, updateNavigation] = useNavigationState();

  const eligibleFreeProduct = useMemo(() => {
    const productKey = cart?.eligibleFreeProductKeys[0]
    return productKey ? catalog.findFirstAvailableProductByKeyFragment(storefrontConfig, productKey) : null;
  }, [catalog, cart?.eligibleFreeProductKeys, storefrontConfig]);

  const hideCart = useCallback(() => {
    updateNavigation(NavigatePage.action(StorefrontPage.Shop, storefrontConfig.usesNewBottomNav));
  }, [updateNavigation, storefrontConfig.usesNewBottomNav]);

  const onCheckoutClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (storefrontConfig.storeTypeRestrictions.vapeHealthWarning) {
      const container = document.getElementById("vapes-notice");
      if (container) {
        container.classList.add("is-active");
      }
    } else {
      checkout(e);
    }
  };

  if (!cart || cart.cartItemCount() <= 0) {
    return (
      <EmptyCart displayingCartAppView={displayingCartAppView} afterpayAllowed={storeHidesAgeRestrictedItems} />
    );
  }

  const cartProductSuggestion = () => {
    if (!eligibleFreeProduct) {
      return <></>;
    }

    return (
      <>
        <div className="container">
          <CartProductSuggestion
            onAddToCart={addItemToCart}
            product={eligibleFreeProduct}
          />
        </div>

        <div className="divider"></div>
      </>
    );
  };

  const checkout = (e : React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSubmitting(true);
    window.location.href = newLookReviewOrderPath();
  }

  const formatter = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
  const cartHeightOffsetDesktop = { "--cart-height-offset-desktop": cartMaxHeightOffset } as React.CSSProperties;

  const checkoutButtonClasses = classNames({
    "button": true,
    "is-primary": true,
    "is-full-width": true,
    "is-loading": isLoading || submitting,
  });

  const checkoutButtonIconClasses = classNames({
    "loading-icon": true,
    "is-scooter": true,
    "is-completely-hidden": !submitting,
  });

  const belowMinimumSubtotal = (cart?.subtotal() ?? 0) > 0 && (cart?.subtotal() ?? 0) < (storefrontConfig.minimumSubtotal*100);
  const checkoutDisabled = cart?.cartItems ? (cart.cartItems.length == 0 || belowMinimumSubtotal) : true
  const checkoutButtonLabel = belowMinimumSubtotal ? `Minimum subtotal ${formatter.format(storefrontConfig.minimumSubtotal).replace('.00', '')}` : "Checkout"

  const showCheckoutButton = () => {
    if (isLoading) {
      return (
      <button className={checkoutButtonClasses} disabled={true}>
        <span className="loading-icon">
        <StreamlineIcon href={loadingSpinnerAnimatedIconUrl} />
        </span>
        <span>Updating...</span>
      </button>
      );
    }

    return (
      <button className={checkoutButtonClasses} disabled={checkoutDisabled} onClick={onCheckoutClick} data-cy="link-to-checkout">
        <span className={checkoutButtonIconClasses}>
          <StreamlineIcon href={scooterAnimatedIconUrl} additionalClasses={{ "is-medium": true }} />
        </span>
        <span>{checkoutButtonLabel}</span>
      </button>
    );
  }

  const cartHeader = () => {
    if (storefrontConfig.usesNewBottomNav) {
      return (
        <FulfillmentPopover />
      );
    } else {
      return (
        <div className="nav has-title">
          <a className="button is-icon-only" onClick={hideCart}>
            <StreamlineIcon href={arrowLeftIconUrl} />
          </a>

          <h1 className="is-medium-text flex-grow-1">Cart</h1>

          <FulfillmentPopover hasCompactTrigger />
        </div>
      );
    }
  }

  const substitutionsDisclaimer = <>
    <hr/>

    <div className="container" data-testid="substitutions-disclaimer">
      <div className="stack">
        <div className="is-flexbox align-items-center is-flex-gap-xs">
          <StreamlineIcon href={synchronizeArrowsSquareIconUrl} additionalClasses={{ "has-fill-important": true }}/>
          <h5><span>Substitutions</span></h5>
        </div>

        <div className="text-overflow-toggle"
             style={{ "--line-clamp": 2, "--collapse-label": "'Read less'" } as React.CSSProperties}>
          <input className="is-completely-hidden" name="expand_cart_substitutions_disclaimer" id="expand_cart_substitutions_disclaimer" type="checkbox"/>

          <p className="is-muted-text is-tiny-text space-mb-none">
            If a product is unavailable — the person packing your order will do their best to find a substitute with the
            same value or less.
            <br/><br/>
            Any substitution price difference will be refunded when the order is complete. If no substitutes are
            available then the full item value will be refunded. Refunds can take 5-7 business days to process.
          </p>

          <label htmlFor="expand_cart_substitutions_disclaimer" className="is-muted-text is-tiny-text">
            <StreamlineIcon href={moveExpandVerticalIconUrl} additionalClasses={{ "is-muted-text": true }}/>
            <span>Read more</span>
          </label>
        </div>
      </div>
    </div>
  </>

  return (
    <div className={"app-view" + (displayingCartAppView && !storefrontConfig.usesNewBottomNav ? " is-active" : "")} id="cart" style={cartHeightOffsetDesktop}>
      {/* displayingCartAppView is-active logic can be removed here once all stores migrated to bottom navigation */}
        <header className="divider has-content has-high-contrast has-divider is-hidden-above-desktop">
          {cartHeader()}
        </header>

        <section className="scrollable-area has-space-top space-pb-none">
          {cartProductSuggestion()}

          <div className="container">
            <CartDisplay
              cart={cart}
              editCartItem={editCartItem}
              removeCartItem={removeCartItem}
            />
          </div>

          <div className="divider"></div>

          <div className="container">
            <div className="line-item">
              <span>Subtotal</span>
              <span>{formatter.format((cart?.subtotal() || 0) / 100)}</span>
            </div>

            <div className="line-item">
              <span>Delivery fee</span>
              <span data-testid="delivery-fee">{formatter.format((cart?.deliveryFee || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.serviceFee || 0) <= 0 })}>
              <span>Service fee</span>
              <span data-testid="service-fee">{formatter.format((cart?.serviceFee || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.subscriptionSavingsOnFees || 0) <= 0 })}>
              <span>🤙 Mates Rates</span>
              <span data-testid="subscription-delivery-fee-discount">{formatter.format((-cart?.subscriptionSavingsOnFees || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.discount || 0) <= 0 })}>
              <span>Total discount</span>
              <span data-testid="discount">{formatter.format((-cart?.discount || 0) / 100)}</span>
            </div>

            <div className="line-item is-large">
              <span>Total</span>
              <span>{formatter.format((cart?.total() || 0) / 100)}</span>
            </div>
          </div>

          {storefrontConfig.showSubstitutionsDisclaimer ? substitutionsDisclaimer : null}

          <div className="is-stuck-bottom">
            <section className="has-gradient">
              <div className="container">
                {showCheckoutButton()}
              </div>
            </section>
          </div>
        </section>
    </div>
  );
}

const EmptyCart = ({ displayingCartAppView, afterpayAllowed }: { displayingCartAppView: boolean; afterpayAllowed: boolean }) => {
  const storefrontConfig = useStorefrontConfig();

  return (
    <div className={"app-view " + (displayingCartAppView && !storefrontConfig.usesNewBottomNav ? "is-visible-above-desktop" : "")} id="cart">
      <div className="card flex-grow-1">
        <section className="container is-flexbox flex-column align-items-center flex-grow-1 justify-content-center">
          <StreamlineIcon href={shoppingBagSideIconUrl} additionalClasses={{ "is-large": true, "has-fill-important": true, "space-mb-sm": true }} />
          <h5>Looking empty</h5>

          <p className="is-tiny-text is-muted-text is-center-aligned-text">Pop a few things in your cart to get started</p>

          <div className="is-flexbox is-flex-gap-md space-pt-sm">
            <img src={VisaMarkIconPath} className="payment-mark responsive-image" alt="Visa credit card icon" />
            <img src={MastercardMarkIconPath} className="payment-mark responsive-image" alt="Mastercard credit card icon" />
            <img src={ApplePayIconPath} className="payment-mark responsive-image" alt="ApplePay credit card icon" />
            <img src={GooglePayIconPath} className="payment-mark responsive-image" alt="GooglePay credit card icon" />
            {afterpayAllowed && <img src={AfterpayMarkIconPath} data-testid="afterpay-mark" className="payment-mark responsive-image" alt="AfterPay credit card icon" />}
          </div>
        </section>
      </div>
    </div>
  );
}

const CartDisplay = ({
  cart,
  editCartItem,
  removeCartItem,
}: {
  cart: Model.Cart;
  editCartItem: (cartItem: Model.CartItem) => void;
  removeCartItem: (cartItem: Model.CartItem) => void;
}) : React.JSX.Element => {
  const cartItems = cart.cartItems.map((ci, index) => {
    const onEdit = () => editCartItem(ci);
    const onRemove = () => removeCartItem(ci);

    if (ci.quantity > 0) {
      return (
        <CartItemDisplay
          cartItem={ci}
          key={index}
          editCartItem={onEdit}
          removeCartItem={onRemove}
        />
      );
    }

  });

  return <>{cartItems}</>;
}

const CartItemDisplay = ({
  cartItem,
  editCartItem,
  removeCartItem,
}: {
  cartItem: Model.CartItem;
  editCartItem: () => void;
  removeCartItem: () => void;
}): React.JSX.Element => {
  const formatter = useMemo(() => {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
    });
  }, [])

  const config = useStorefrontConfig();

  const extras = useMemo(() => cartItem.extrasFullDescription(),[cartItem])

  const extrasArrayMappedForPTags = extras.map((e, index) => {
    return <p className="is-tiny-text" key={index}>{e}</p>
  })

  const outOfStockResolutionPreference = useMemo(() => {
    if (!config.allowSubstitutionPreferences) return null;

    return (
      <div className="is-flexbox align-items-center is-flex-gap-xs substitution-preferences">
        <StreamlineIcon href={synchronizeArrowsSquareIconUrl} additionalClasses={{ "has-fill-important": true }} />
        <p className="is-muted-text">{cartItem.outOfStuckResolutionPreference.title}</p>
      </div>
    );
  }, [cartItem.outOfStuckResolutionPreference.title, config.allowSubstitutionPreferences])

  const appliedDiscountBadge = useMemo(() => {
    if (!cartItem.isDiscounted()) {
      return null;
    }

    return (
      <div className="is-flexbox flex-column flex-grow-1 align-items-start">
      {
        Object.entries(cartItem.discounts).map(([couponName, discount]) => {
          return (
            <div key={couponName} className="badge is-important">
              <StreamlineIcon href={tagsFavoriteIconUrl} />
              <span>{discount.name}</span>
            </div>
          );
        })
      }
      </div>
    );
  }, [cartItem])

  const priceDescriptor = useMemo(() => {
    const originalSubtotal = cartItem.subtotal();
    const discountedPrice = cartItem.discountedPrice()

    if (originalSubtotal != discountedPrice) {
      const descriptor = discountedPrice == 0 ? "FREE" : formatter.format(discountedPrice / 100.0)

      return (
        <div className="price">{descriptor} <s className="is-muted-text">{formatter.format(originalSubtotal / 100.0)}</s></div>
      );
    } else {
      return (
        <div className="price">{formatter.format((cartItem.subtotal()) / 100.0)}</div>
      );
    }
  }, [cartItem, formatter])

  return (
    <div className="line-item has-full-description" data-cy={"order-item"}>
      <div className="is-flexbox flex-grow-1 align-items-start">
        <div className="title">{cartItem.name}</div>
        <div className="badge is-quantity">{cartItem.quantity}</div>
      </div>

      <div className="is-flexbox flex-column is-flex-gap-xs flex-grow-1">
        <div className="is-flexbox flex-column flex-grow-1 align-items-start">
          {
            config.storefrontExperience.hideCartDescriptions || !cartItem.description ? <></> :
            <div className="description">
              <p className="is-tiny-text is-muted-text space-pb-sm">{cartItem.description}</p>
            </div>
          }

          {extrasArrayMappedForPTags.length !== 0 ? <div className="item-options">{extrasArrayMappedForPTags}</div> : <></>}

          {cartItem.mealPreferences ? <div className="meal-preference" data-testid="meal-preference"><p className="is-tiny-text is-muted-text">{cartItem.mealPreferences}</p></div> : <></>}
        </div>

        {appliedDiscountBadge}

        <div className="is-flexbox align-items-center">
          {priceDescriptor}

          <a className="chip is-action" onClick={editCartItem}>
            <StreamlineIcon href={pencilIconUrl} />
            <span>Edit</span>
          </a>

          <a className="chip is-action" onClick={removeCartItem}>
            <StreamlineIcon href={bin2IconUrl} />
            <span>Delete</span>
          </a>
        </div>

        {outOfStockResolutionPreference}
      </div>
    </div>
  );
}

const CartProductSuggestion = ({ product, onAddToCart }: { product: Product; onAddToCart: (product: Product) => Promise<void> }): React.JSX.Element => {
  const formatter = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  });

  const addToCart = useCallback(async () => {
    await onAddToCart(product);
  }, [onAddToCart, product]);

  const [primaryTags] = buildTagElementsForTags(product.tags);

  return (
    <div className="line-item has-full-description">
      <div className="title">
        <StreamlineIcon href={tagsFavoriteIconUrl} additionalClasses={{ "has-fill-important": true }} />
        &nbsp;
        {product.title}
      </div>
      <div className="description">
        <p className="is-tiny-text is-muted-text">{product.description}</p>
      </div>
      <div className="price">FREE <s className="is-muted-text">{formatter.format((product.price) / 100.0)}</s></div>
      {primaryTags}
      <button className={"button is-full-width"} onClick={addToCart} data-cy={"add-suggestion-to-cart-button"}>
        <span>Add to cart</span>
      </button>
    </div>
  );
}


export { Cart };
